import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import useSWR from 'swr';
import { useUser, useGlobal } from 'src/context';
import { secondRoundCandidates } from './static';
import { getVoteStatus } from 'src/api';
import VoteDialog from './VoteDialog';

function getVoteBtnText(params) {
  const { isEnded, isOpening, isQualified, isSectionVoted, isLogin } = params;

  if (isEnded) {
    return "投票期間已結束";
  } else if (isOpening) {
    if (isQualified) {
      return isSectionVoted ? "已投票" : "進行投票";
    } else {
      return isLogin ? "資格不符" : "請登入確認資格";
    }
  } else {
    return "活動尚未開始";
  }
}

const CandidatesSection = () => {
  const { isLoadingUser, isLogin } = useUser();
  const { onOpenLogin } = useGlobal();
  const [votingSection, setVotingSection] = useState(null);

  const { data, isLoading, mutate } = useSWR(`goty2024-voteStatus`, getVoteStatus, {
    refreshInterval: 6 * 60 * 1000, // 6 minutes
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
  });

  useEffect(() => {
    if (isLogin) {
      mutate();
    }
  }, [isLogin]);

  const voteStatus = data?.data || {};

  const latestPollStat = voteStatus.stats || {};
  const hasVotedGroup = voteStatus.voted || {};
  const isQualified = voteStatus.isVotable || false;
  const isOpening = voteStatus.event === "OPEN_SECOND";
  const isEnded = voteStatus.event === "END";

  const isForbidden = !isOpening || isLoadingUser || isLoading || !isQualified;
  const candidateGroups = Object.keys(secondRoundCandidates);

  const handleVoteClick = (groupKey) => {
    if (!isLoadingUser && !isLogin) {
      onOpenLogin();
      return;
    }

    if (isForbidden || hasVotedGroup?.[groupKey]) return;

    setVotingSection(groupKey);
  }

  const handleCloseDialog = () => {
    setVotingSection(null);
  }

  const handleVoteSubmitted = () => {
    mutate();
  }

  return (
    <>
      <section id="candidates" className="py-10 bg-[#317E70]">
        <div className="container mx-auto px-4 flex flex-col gap-y-12">
          <h2 className="text-4xl font-bold text-center text-white mb-4">入圍名單</h2>

          {candidateGroups.map((groupKey, index) => {
            const { name, description, candidates } = secondRoundCandidates[groupKey];
            const isBtnDisabled = isForbidden || hasVotedGroup?.[groupKey] || isEnded;

            if (groupKey === 'rookies') {
              return (
                <div id={groupKey} key={groupKey} className='md:rounded-3xl bg-[#7B2337] p-4 md:p-8'>
                  <h3 className='my-6 md:mt-0 md:p-8 text-white md:text-[#2B1923] text-2xl md:text-4xl md:rounded-3xl md:bg-[#DFB750] text-center font-bold'>{index + 1}. {name}</h3>
                  <p className='mb-6 md:px-8 text-lg md:text-xl text-white font-medium'>參考指標：{description}</p>
                  <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 justify-items-center gap-8 pb-6'>
                    {candidates.map((firmData) => {
                      const { id, title, logoUrl } = firmData;
                      const count = latestPollStat?.[groupKey]?.[id] ?? 0;
                      const isChosen = hasVotedGroup?.[groupKey]?.value?.includes(id);
                      return (
                        <div key={`firm-${id}`} className={clsx('relative w-full max-w-[354px] bg-black/20 rounded-lg p-4')}>
                          {isChosen && (
                            <div className="absolute -top-2 -right-2 z-20">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-yellow-400">
                                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                                </svg>
                            </div>
                          )}
                          <Link href={`/firm/${id}`} className="flex items-center gap-4">
                            <img src={logoUrl} className="w-16 h-16 rounded-full object-cover bg-white" alt={title} />
                            <div className="text-white">
                              <h5 className='text-lg font-bold leading-6'>{title}</h5>
                            </div>
                          </Link>
                          {(isOpening || isEnded) && (
                            <p className="text-xl font-bold text-right text-white mt-2">{count} 票</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-center mt-4">
                    <button
                      className='self-center w-full max-w-[354px] h-fit py-4 bg-[#0D6072] disabled:bg-gray-400 border-none rounded-2xl text-white text-xl font-medium disabled:cursor-not-allowed'
                      disabled={isBtnDisabled}
                      onClick={() => handleVoteClick(groupKey)}
                    >
                      {getVoteBtnText({ isEnded, isOpening, isQualified, isSectionVoted: hasVotedGroup?.[groupKey], isLogin })}
                    </button>
                  </div>
                </div>
              );
            }

            if (groupKey === 'popularity') {
              const processedCandidates = candidates
                .map(c => ({ ...c, count: latestPollStat?.[groupKey]?.[c.id] ?? 0 }))
                .filter(c => c.count > 0)
                .sort((a, b) => b.count - a.count)
                .slice(0, 15);

              return (
                <div id={groupKey} key={groupKey} className='md:rounded-3xl bg-[#7B2337] p-4 md:p-8'>
                  <h3 className='my-6 md:mt-0 md:p-8 text-white md:text-[#2B1923] text-xl md:text-4xl md:rounded-3xl md:bg-[#DFB750] text-center font-bold'>{index + 1}. {name}</h3>
                  <p className='mb-2 md:px-8 text-lg md:text-xl text-white font-medium'>參考指標：{description}</p>
                  <p className='mb-6 md:px-8 text-base text-white'>投票期間僅顯示得票數前 15 名</p>
                  <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 justify-items-center gap-8 pb-6'>
                    {processedCandidates.map((gameData) => {
                      const { id, title, firmName, count } = gameData;
                      const isChosen = hasVotedGroup?.[groupKey]?.value?.includes(id);

                      return (
                        <div key={`game-${groupKey}-${id}`} className={clsx('relative w-full max-w-[354px] bg-black/20 rounded-lg p-4')}>
                          {isChosen && (
                            <div className="absolute -top-2 -right-2 z-20">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-yellow-400">
                                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                                </svg>
                            </div>
                          )}
                          <div className='flex justify-between items-center text-white'>
                            <div>
                              <Link href={`/game/${id}`}>
                                <h5 className='text-lg font-bold leading-6 hover:underline'>{title}</h5>
                              </Link>
                              <h6 className='text-sm font-medium'>{firmName}</h6>
                            </div>
                            {(isOpening || isEnded) && (
                              <p className="text-xl font-bold text-right">{count} 票</p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="flex justify-center mt-4">
                    <button
                      className='self-center w-full max-w-[354px] h-fit py-4 bg-[#0D6072] disabled:bg-gray-400 border-none rounded-2xl text-white text-xl font-medium disabled:cursor-not-allowed'
                      disabled={isBtnDisabled}
                      onClick={() => handleVoteClick(groupKey)}
                    >
                      {getVoteBtnText({
                        isEnded,
                        isOpening,
                        isQualified,
                        isSectionVoted: hasVotedGroup?.[groupKey],
                        isLogin,
                      })}
                    </button>
                  </div>
                </div>
              )
            }

            return (
              <div
                id={groupKey}
                key={groupKey}
                className='md:rounded-3xl bg-[#7B2337] p-4 md:p-8'
              >
                <h3 className='my-6 md:mt-0 md:p-8 text-white md:text-[#2B1923] text-xl md:text-4xl md:rounded-3xl md:bg-[#DFB750] text-center font-bold'>{index + 1}. {name}</h3>
                <p className='mb-6 md:px-8 text-lg md:text-xl text-white font-medium'>參考指標：{description}</p>
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 justify-items-center gap-8 pb-6'>
                  {candidates.map((gameData) => {
                    const { id, title, gameBannerSrc, firmName } = gameData;
                    const count = latestPollStat?.[groupKey]?.[id] ?? 0;
                    const isChosen = hasVotedGroup?.[groupKey]?.value?.includes(id);

                    return (
                      <div key={`game-${groupKey}-${id}`} className={clsx('relative w-full max-w-[354px] bg-black/20 rounded-lg')}>
                        {isChosen && (
                          <div className="absolute -top-2 -right-2 z-20">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-yellow-400">
                                <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        <Link href={`/game/${id}`} className='block overflow-hidden'>
                          <img src={gameBannerSrc} className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300 rounded-t-lg" alt={title} />
                        </Link>
                        <div className='p-4'>
                          <div className='flex justify-between items-center text-white'>
                            <div>
                              <h5 className='text-lg font-bold leading-6'>{title}</h5>
                              <h6 className='text-sm font-medium'>{firmName}</h6>
                            </div>
                            {(isOpening || isEnded) && (
                              <p className="text-xl font-bold text-right">{count} 票</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className="flex justify-center mt-4">
                  <button
                    className='self-center w-full max-w-[354px] h-fit py-4 bg-[#0D6072] disabled:bg-gray-400 border-none rounded-2xl text-white text-xl font-medium disabled:cursor-not-allowed'
                    disabled={isBtnDisabled}
                    onClick={() => handleVoteClick(groupKey)}
                  >
                    {getVoteBtnText({
                      isEnded,
                      isOpening,
                      isQualified,
                      isSectionVoted: hasVotedGroup?.[groupKey],
                      isLogin,
                    })}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </section>
      <VoteDialog 
        isOpen={!!votingSection}
        onClose={handleCloseDialog}
        sectionKey={votingSection}
        onSubmitted={handleVoteSubmitted}
      />
    </>
  );
};

export default CandidatesSection;
